import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Message from '@/components/ui/chat/Message/Message.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Loading } from 'react-daisyui';
import { ChatMessage } from '@/models/ChatMessage';
import { cn } from '@/utils/classNames.ts';
import { useSearchParams } from 'react-router-dom';
import SearchParams from '@/_constants/SearchParams.ts';
import ChatRole from '@/_constants/ChatRole.ts';
import { queryClient } from '@/main.tsx';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import ProductFeature from '@/_constants/ProductFeature.ts';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import SearchResult from '@/components/ui/SearchResult/SearchResult.tsx';
import { useSearchContext } from '@/context/SearchContext.tsx';
import type { Chat } from '@/models/Chat.d.ts';
import SubscriberUpgradeModal from '@/components/modals/SubscriberUpgradeModal/SubscriberUpgradeModal.tsx';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import LimitType from '@/_constants/LimitType.ts';

const chatSchema = z.object({
  message: z.string().min(1, { message: 'Message can not be empty.' }),
});

type ChatSchema = z.infer<typeof chatSchema>;

type ChatProps = {
  chat: Chat;
  focus?: boolean;
  scrollChatFn?: () => void;
  hasAiResponsesLimitFeature: boolean;
  hasSearchLimitFeature: boolean;
  hasSubscription: boolean;
};

const Chat: FC<ChatProps> = ({
  chat,
  focus = false,
  scrollChatFn,
  hasAiResponsesLimitFeature,
  hasSearchLimitFeature,
  hasSubscription,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeChatId = chat.id;
  const activeChatName = chat.name;
  const forceResponseFromChat = searchParams.get(SearchParams.FORCE_RESPONSE) === '1';
  const {
    storageConsent,
    togglePinItem,
    addSearchResult,
    pinnedItemIdsWithTimestamp,
    isLoadingUserContext,
    localUserContext,
    chatWithBot,
    isLoadingChatWithBot,
    errorChatWithBot,
    chatMessages,
    isLoadingChatMessages,
    errorChatMessages,
    localChatMessagesStore,
    setLocalChatMessagesStore,
  } = useSearchContext();
  const { trackLimitReached, trackUpgradeModalOpened } = useAnalytics();
  const [isSubscriberUpgradeModalOpen, setIsSubscriberUpgradeModalOpen] = useState(false);

  const chatWrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { control, handleSubmit, reset } = useForm<ChatSchema>({
    resolver: zodResolver(chatSchema),
    defaultValues: {
      message: '',
    },
  });

  const localChatMessages = localChatMessagesStore[activeChatId] || [];

  const scrollChatToBottom = useCallback(() => {
    if (scrollChatFn) scrollChatFn();
  }, [scrollChatFn]);

  const isLoading = isLoadingChatMessages || isLoadingChatWithBot || isLoadingUserContext;

  const isLastMessageLimit = !!localChatMessages?.[localChatMessages.length - 1]?.meta?.limit;

  const limitType = localChatMessages?.[localChatMessages.length - 1]?.meta?.limit;

  const hasLimitFromLastMessage =
    hasSubscription ||
    !limitType ||
    (limitType === ProductFeature.AI_RESPONSES
      ? hasAiResponsesLimitFeature
      : limitType === ProductFeature.SEARCHES
        ? hasSearchLimitFeature
        : true);

  const requestResponseFromChat = useCallback(
    async (message?: string | null) => {
      const response = await chatWithBot({
        message,
        chat_id: activeChatId,
        user_context: localUserContext,
      });

      await queryClient.invalidateQueries({ queryKey: ['user'] });

      response.map((chatMessage) => {
        if (chatMessage.meta?.subscription_limit) {
          trackUpgradeModalOpened('subscriber_limit');
          setIsSubscriberUpgradeModalOpen(true);
        }
        if (chatMessage.meta?.limit) {
          const limitType =
            chatMessage.meta.limit === LimitType.SEARCHES
              ? 'username_search'
              : chatMessage.meta.limit === LimitType.AI_RESPONSES
                ? 'ai_responses'
                : undefined;
          if (limitType) {
            trackLimitReached(limitType);
          }
        }
        if (!chatMessage.meta?.search_result) return;
        addSearchResult(chatMessage.meta.search_result);
      });

      setLocalChatMessagesStore((prev) => {
        const existingMessages = prev[activeChatId] || [];
        return {
          ...prev,
          [activeChatId]: [...existingMessages, ...response],
        };
      });
      requestAnimationFrame(scrollChatToBottom);
    },
    [activeChatId, addSearchResult, localUserContext]
  );

  const handleSendMessage = (data: ChatSchema) => {
    void (async () => {
      try {
        if (!data.message || isLoadingChatWithBot) return;
        reset();

        setLocalChatMessagesStore((prev) => {
          const existingMessages = prev[activeChatId] || [];
          return {
            ...prev,
            [activeChatId]: [
              ...existingMessages,
              {
                id: `${Math.random()}_${activeChatId}`,
                content: data.message,
                role: 'user',
                chat_id: activeChatId,
                user_only: false,
                created_at: new Date().toISOString(),
              },
            ],
          };
        });

        requestAnimationFrame(scrollChatToBottom);

        await requestResponseFromChat(data.message);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleGoToPaymentPage = () => {
    const redirectUrl = new URL(window.location.href);
    redirectUrl.searchParams.set(SearchParams.FORCE_RESPONSE, '1');

    window.location.href = passToRouteSearchParams(`${import.meta.env.VITE_FRONTEND_URL}/payment-select`, {
      [SearchParams.REDIRECT_URL]: encodeURIComponent(redirectUrl.toString()),
    });
  };

  const handlePinClick = useCallback(
    (chatMessage: ChatMessage) => {
      if (!chatMessage.meta?.search_result) return;

      if (storageConsent) {
        togglePinItem(chatMessage.meta.search_result.id);
        return;
      }

      addSearchResult(chatMessage.meta.search_result);
      togglePinItem(chatMessage.meta.search_result.id);
    },
    [addSearchResult, storageConsent, togglePinItem]
  );

  // request response on chat load when last message is from user
  useEffect(() => {
    if (!chatMessages || !forceResponseFromChat) return;

    setSearchParams((prev) => {
      prev.set(SearchParams.FORCE_RESPONSE, '0');
      return prev;
    });

    void (async () => {
      try {
        await requestResponseFromChat();
      } catch (error) {
        console.error(error);
      }
    })();
  }, [chatMessages, requestResponseFromChat, forceResponseFromChat, setSearchParams]);

  useLayoutEffect(() => {
    if (focus && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [focus]);

  // fetch initial chat messages from server and save it in store
  useEffect(() => {
    if (!chatMessages) return;

    setLocalChatMessagesStore((prev) => {
      if (prev[activeChatId]) return prev;

      return {
        ...prev,
        [activeChatId]: chatMessages,
      };
    });

    requestAnimationFrame(scrollChatToBottom);
  }, [chatMessages, scrollChatToBottom]);

  useLayoutEffect(() => {
    scrollChatToBottom();
    setTimeout(() => {
      scrollChatToBottom();
    }, 500);
  }, [activeChatId, scrollChatToBottom]);

  useEffect(() => {
    if (!activeChatName) return;

    if (activeChatName === t('components.chat.newChat')) {
      void queryClient.invalidateQueries({ queryKey: ['chat_messages', activeChatId] });
    }
  }, [activeChatId, activeChatName]);

  useEffect(() => {
    async function invalidateUserFeaturesIfLimit() {
      if (!isLastMessageLimit) return;

      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({ queryKey: ['chat_messages', activeChatId] });
      requestAnimationFrame(scrollChatToBottom);
    }

    void invalidateUserFeaturesIfLimit();
  }, [isLastMessageLimit]);

  return (
    <div ref={chatWrapperRef} className="flex flex-col grow justify-end relative pt-4 md:pt-10 lg:pt-12">
      {isSubscriberUpgradeModalOpen && (
        <SubscriberUpgradeModal
          isOpen={isSubscriberUpgradeModalOpen}
          onClose={() => setIsSubscriberUpgradeModalOpen(false)}
        />
      )}
      <div className="pb-4">
        {errorChatMessages ? <Alert status="error">{errorChatMessages.message}</Alert> : null}
        {errorChatWithBot ? <Alert status="error">{errorChatWithBot.message}</Alert> : null}
        {isLoadingChatMessages ? (
          [...Array(6)].map((_, index) =>
            index % 2 ? (
              <Message.Skeleton key={`message_skeleton_left_${index}`} />
            ) : (
              <Message.Skeleton key={`message_skeleton_right_${index}`} end />
            )
          )
        ) : localChatMessages.length > 0 ? (
          localChatMessages?.map((chatMessage, index) => {
            if (chatMessage.role === ChatRole.ASSISTANT) {
              return (
                <Message.Bot
                  key={chatMessage.id}
                  message={chatMessage.content}
                  date={chatMessage.created_at}
                  renderUnderMessage={
                    <>
                      {chatMessage.meta?.search_result ? (
                        <SearchResult
                          phrase={chatMessage.meta.search_result.phrase}
                          tld_pref={chatMessage.meta.search_result.tld_pref}
                          domains={chatMessage.meta.search_result.domains}
                          socialMedia={chatMessage.meta.search_result.socialMedia}
                          onPinClick={() => handlePinClick(chatMessage)}
                          isPinned={pinnedItemIdsWithTimestamp
                            .map((item) => item.id)
                            .includes(chatMessage.meta.search_result.id)}
                        />
                      ) : null}
                      {!hasLimitFromLastMessage && localChatMessages.length === index + 1 ? (
                        <div className="flex justify-center">
                          <Button onClick={handleGoToPaymentPage} color="primary">
                            {t('button.upgrade')}
                          </Button>
                        </div>
                      ) : null}
                    </>
                  }
                />
              );
            }
            return (
              <Message.User
                key={chatMessage.id}
                name={t('components.chat.you')}
                message={chatMessage.content}
                date={chatMessage.created_at}
              />
            );
          })
        ) : (
          <div className="flex flex-col gap-4">
            <LoadingButton
              onClick={() => handleSendMessage({ message: t('components.chat.button.checkAvailability') })}
              loading={isLoading}
              color="primary"
            >
              {t('components.chat.button.checkAvailability')}
            </LoadingButton>
            <LoadingButton
              onClick={() => handleSendMessage({ message: t('components.chat.button.helpFindBrandName') })}
              loading={isLoading}
              color="primary"
            >
              {t('components.chat.button.helpFindBrandName')}
            </LoadingButton>
            <LoadingButton
              onClick={() => handleSendMessage({ message: t('components.chat.button.nameIdeas') })}
              loading={isLoading}
              color="primary"
            >
              {t('components.chat.button.nameIdeas')}
            </LoadingButton>
          </div>
        )}
        {isLoadingChatWithBot ? <Message.Bot message={<Loading variant="dots" />} /> : null}
      </div>
      <div className="sticky bottom-0 z-10">
        <form className="pb-4 md:pb-10 lg:pb-12 pt-4 bg-base-200" onSubmit={handleSubmit(handleSendMessage)}>
          <Controller
            control={control}
            name="message"
            render={({ field: { onChange, value } }) => (
              <TextInput
                readOnly={isLoading || !hasLimitFromLastMessage}
                ref={inputRef}
                value={value}
                onChange={onChange}
                placeholder={t('placeholder.sendMessageToBrandsnag')}
                size="lg"
                endIcon={
                  <button type="submit">
                    <PaperAirplaneIcon
                      className={cn('w-4', {
                        'text-text-secondary': !isLoading,
                        'text-gray-300': isLoading,
                      })}
                    />
                  </button>
                }
              />
            )}
          />
        </form>
      </div>
    </div>
  );
};

export default Chat;
